import React, { useEffect, useRef, useMemo } from 'react';
import { AbsoluteFill, useVideoConfig, useCurrentFrame } from 'remotion';
import { useVisualizerData } from '../audio-visualizer/hooks/use-visualizer-data';
import { getTrackSchedule } from '../audio-visualizer/utils/audio';
import { CROSSFADE_DURATION_FRAMES } from '../constants';
import { AudioSpectrumProps } from './constants';

export const AudioSpectrum: React.FC<AudioSpectrumProps> = ({
    audioTracks,
    spectrum,
    title = { show: true, position: 'bottom', gap: 20 }, // Default if undefined
    isTest,
}) => {
    const frame = useCurrentFrame();
    const { fps } = useVideoConfig();
    const canvasRef = useRef<HTMLCanvasElement>(null);

    // Configuration
    const config = spectrum;

    // Calculate schedule
    const safeTrackDurations = useMemo(() => {
        return audioTracks.map((track) => {
            return track.durationInSeconds
                ? Math.ceil(track.durationInSeconds * fps)
                : 30 * fps;
        });
    }, [audioTracks, fps]);

    const schedule = useMemo(
        () => getTrackSchedule(safeTrackDurations, CROSSFADE_DURATION_FRAMES),
        [safeTrackDurations],
    );

    // Get Audio Data
    const bars = useVisualizerData({
        audioTracks,
        schedule,
        barsToDisplay: config.freqBands,
        isTest,
    });

    // Calculate current title
    const currentTitle = useMemo(() => {
        if (!title.show) return null;

        const active = [...schedule]
            .reverse()
            .find((s) => frame >= s.startFrame && frame < s.endFrame);
        return active ? audioTracks[active.index]?.title : null;
    }, [title, schedule, frame, audioTracks]);

    // Dimensions Calculation
    // We calculate the precise size needed for the spectrum
    // totalWidth = (N * thickness) + ((N-1) * gap)
    const spectrumWidth = config.freqBands * config.thickness + (config.freqBands - 1) * config.gap;

    // Height needs to accommodate the max height * 2 (for bidirectional) or * 1 (for unidirectional)
    // plus softness glow. Let's aim for safe bounds.
    // If side is side_ab, height is maxHeight * 2. If a/b, it's maxHeight.
    // However, canvas logic currently draws somewhat centered. Let's force a strict height.
    // Height needs to accommodate the max height * 2 (for bidirectional) or * 1 (for unidirectional)
    // plus softness glow. Let's aim for safe bounds.
    // If side is side_ab, height is maxHeight * 2. If a/b, it's maxHeight.
    // However, canvas logic currently draws somewhat centered. Let's force a strict height.
    const spectrumHeight = config.side === 'side_ab' ? config.maxHeight * 2 : config.maxHeight;
    const canvasHeight = spectrumHeight + (config.softness * 2); // Padding only for glow
    const canvasWidth = spectrumWidth + (config.softness * 2); // Padding only for glow

    // Drawing Logic
    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        // Clear local canvas
        ctx.clearRect(0, 0, canvasWidth, canvasHeight);

        // Center calculation within the local canvas
        let localCenterY = canvasHeight / 2;

        if (config.side === 'side_a') localCenterY = canvasHeight - config.softness; // Bottom anchored (strict)
        if (config.side === 'side_b') localCenterY = config.softness;               // Top anchored (strict)

        // Setup Gradient
        const gradientHeight = config.maxHeight > 0 ? config.maxHeight : 100;
        const gradient = ctx.createLinearGradient(0, localCenterY - gradientHeight, 0, localCenterY + gradientHeight);
        gradient.addColorStop(0, config.outsideColor);
        gradient.addColorStop(0.5, config.insideColor);
        gradient.addColorStop(1, config.outsideColor);

        ctx.fillStyle = gradient;
        ctx.strokeStyle = gradient;
        ctx.lineWidth = config.thickness;
        ctx.lineCap = 'round';

        // Filter Softness
        if (config.softness > 0) {
            ctx.shadowBlur = config.softness / 2;
            ctx.shadowColor = config.insideColor;
        } else {
            ctx.shadowBlur = 0;
            ctx.shadowColor = "transparent";
        }

        // Draw start X (left edge of the actual bars)
        const startX = (canvasWidth - spectrumWidth) / 2; // Should align with localCenterX logic

        // --- DRAWING MODES ---
        bars.forEach((v, i) => {
            const val = isNaN(v) ? 0 : v;
            let barHeight = val * config.maxHeight;
            // Apply minHeight if val > 0 (or even if 0 to show presence?)
            // Usually minHeight is for visibility.
            barHeight = Math.max(barHeight, config.minHeight);

            const posX = startX + i * (config.thickness + config.gap); // Correct X based on reduced canvas size

            if (config.display === "digital") {
                if (config.side === "side_ab") {
                    // Draw one continuous bar to ensure no gap/seam at the center
                    ctx.fillRect(posX, localCenterY - barHeight, config.thickness, barHeight * 2);
                } else {
                    if (config.side === "side_a") {
                        ctx.fillRect(posX, localCenterY - barHeight, config.thickness, barHeight);
                    }
                    if (config.side === "side_b") {
                        ctx.fillRect(posX, localCenterY, config.thickness, barHeight);
                    }
                }
            } else if (config.display === "analog_lines") {
                if (i === 0) ctx.beginPath(); // Start path properly? Actually analog lines usually need continuous path
                // Re-implementing path logic for loop:
                // We need to begin path outside loop or manage moveTo/lineTo
            }
        });

        // Analog Lines separate loop for path continuity
        if (config.display === 'analog_lines') {
            const drawLinePath = (direction: 'up' | 'down') => {
                ctx.beginPath();
                bars.forEach((v, i) => {
                    const val = isNaN(v) ? 0 : v;
                    const barHeight = val * config.maxHeight;
                    const posX = startX + i * (config.thickness + config.gap);
                    const y = direction === 'up' ? localCenterY - barHeight : localCenterY + barHeight;

                    if (i === 0) ctx.moveTo(posX, y);
                    else ctx.lineTo(posX, y);
                });
                ctx.stroke();
            };

            if (config.side === "side_a" || config.side === "side_ab") drawLinePath('up');
            if (config.side === "side_b" || config.side === "side_ab") drawLinePath('down');
        }

        if (config.display === 'analog_dots') {
            bars.forEach((v, i) => {
                const val = isNaN(v) ? 0 : v;
                const barHeight = val * config.maxHeight;
                const posX = startX + i * (config.thickness + config.gap);

                if (config.side === "side_a" || config.side === "side_ab") {
                    ctx.beginPath();
                    ctx.arc(posX, localCenterY - barHeight, config.thickness / 2, 0, Math.PI * 2);
                    ctx.fill();
                }
                if (config.side === "side_b" || config.side === "side_ab") {
                    ctx.beginPath();
                    ctx.arc(posX, localCenterY + barHeight, config.thickness / 2, 0, Math.PI * 2);
                    ctx.fill();
                }
            });
        }

    }, [frame, bars, config, canvasWidth, canvasHeight]);

    // Layout Logic for Container
    // We utilize a single flex container that shrinks to fit content (width: auto).
    // It is positioned absolutely on the screen based on 'spectrum.position' (9 spots).
    // Flex direction is handled by 'title.position'.

    // 1. Base Container Style (Auto Sizing & Flex)
    const containerStyle: React.CSSProperties = {
        position: 'absolute',
        display: 'flex',
        gap: title.gap,
        // No fixed width, no padding. Let content determine size.
        width: 'auto',
        justifyContent: 'center', // Default align
        alignItems: 'center',     // Default align
    };

    // 2. Flex Direction (Title vs Spectrum)
    // Left/Right: Row. Top/Bottom: Column.
    const isRow = title.position === 'left' || title.position === 'right';
    containerStyle.flexDirection = isRow ? 'row' : 'column';

    // 3. Absolute Positioning (9 Spots)
    // We map each position to exact top/left/right/bottom/transform coordinates.
    const pos = config.position;

    // Helper to center horizontally or vertically
    const centerX = { left: '50%', transform: 'translateX(-50%)' };
    const centerY = { top: '50%', transform: 'translateY(-50%)' };
    const centerXY = { top: '50%', left: '50%', transform: 'translate(-50%, -50%)' };

    // Margins
    const m = 50; // Side margin
    const vm = 100; // Vertical margin (bottom/top)

    switch (pos) {
        // TOP
        case 'top-left':
            containerStyle.top = vm;
            containerStyle.left = m;
            break;
        case 'top-center':
            containerStyle.top = vm;
            Object.assign(containerStyle, centerX);
            break;
        case 'top-right':
            containerStyle.top = vm;
            containerStyle.right = m;
            break;

        // MIDDLE
        case 'middle-left':
            containerStyle.left = m;
            Object.assign(containerStyle, centerY);
            break;
        case 'middle-center':
            Object.assign(containerStyle, centerXY);
            break;
        case 'middle-right':
            containerStyle.right = m;
            Object.assign(containerStyle, centerY);
            break;

        // BOTTOM
        case 'bottom-left':
            containerStyle.bottom = vm;
            containerStyle.left = m;
            break;
        case 'bottom-center':
            containerStyle.bottom = vm;
            Object.assign(containerStyle, centerX);
            break;
        case 'bottom-right':
            containerStyle.bottom = vm;
            containerStyle.right = m;
            break;
    }

    const TitleNode = title.show && currentTitle ? (
        <div style={{
            color: title.style?.color || config.insideColor,
            fontFamily: "sans-serif",
            fontSize: title.style?.fontSize,
            fontWeight: 600,
            textTransform: "uppercase",
            letterSpacing: "0.1em",
            opacity: 0.9,
            whiteSpace: 'nowrap',
            lineHeight: 1,
        }}>
            {currentTitle}
        </div>
    ) : null;

    // left (row): [Title] [Canvas] -> OK.
    // right (row-reverse): [Title] [Canvas] -> Title @ Right, Canvas @ Left. OK.
    // top (col-reverse): [Title] [Canvas] -> Title @ Bottom (Start is Bottom). Canvas @ Top. WRONG. col-reverse 'start' is bottom.
    // col-reverse places Item 1 at Bottom.
    // We want Title Top. So Title should be Item 2 (End).

    // Let's Just Use Conditional DOM Order + Simple Flex.
    // It's less brain twisting.
    // Row: Title Left? Title First. Title Right? Spectrum First.
    // Col: Title Top? Title First. Title Bottom? Spectrum First.

    const renderTitleFirst = title.position === 'left' || title.position === 'top';

    // Reset Flex Direction to simple row/column
    containerStyle.flexDirection = (title.position === 'left' || title.position === 'right') ? 'row' : 'column';

    return (
        <AbsoluteFill>
            <div style={containerStyle}>
                {renderTitleFirst && TitleNode}
                <canvas
                    ref={canvasRef}
                    width={canvasWidth}
                    height={canvasHeight}
                    style={{
                        display: 'block', // Remove inline element spacing
                    }}
                />
                {!renderTitleFirst && TitleNode}
            </div>
        </AbsoluteFill>
    );
};
